import React, { useEffect } from 'react';
import { CBadge, CDropdown, CDropdownItem, CDropdownMenu, CDropdownToggle } from '@coreui/react';
import { useDispatch, useSelector } from 'react-redux';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { capitalize } from 'lodash';
import usePortal from 'react-useportal';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useHistory } from 'react-router-dom';
import { Job, JobPhaseLabel, JobPhaseOrder } from '../../shared/models/jobs';
import { CancelJob, CompleteJob, RemoveJob, StartJob, UpdateJobPhase } from '../actions';
import { OpenModal } from '../../store/actions/modal.action';
import { EditJobDataBag, ModalName, ReassignJobDataBag, ViewJobImagesDataBag } from '../../shared/models/modal';
import { selectJourneyConfigChatEnabled } from '../../store/selectors/journey.selectors';
import { SubscribeChannel } from '../../chat/actions/chat.action';
import { selectChatData } from '../../chat/selectors/chat.selector';
import { mapJobToChatInfo } from '../../shared/models/chat';

export interface JobActionsProps {
  job: Job;
}

const JobActions = ({ job }: JobActionsProps) => {
  const { _id: id, agent_id, phase, start_time, completed, images } = job;
  const { Portal } = usePortal({
    bindTo: document && document.querySelector('body')
  });

  const {
    enableOrgHierarchyModule,
    enableChatModule,
    enableViewOnlyMode,
    enableAssignJobsMode,
  } = useFlags();
  const chatEnabled = useSelector(selectJourneyConfigChatEnabled);
  const chat = useSelector(selectChatData)(`${job?.chat_channel}`);

  const dispatch = useDispatch();
  const history = useHistory();

  const canViewChat = job?.chat_channel > 0 && enableChatModule && chatEnabled && !completed && !!start_time;

  useEffect(() => {
    if (canViewChat && !chat) {
      dispatch(new SubscribeChannel({
        chat_channel: `${job.chat_channel}`,
        chatInfo: mapJobToChatInfo(job),
      }));
    }
  }, [canViewChat, job?.chat_channel]);

  const handleStart = jobID => () => {
    dispatch(new StartJob({
      id: jobID,
      includeChildren: enableOrgHierarchyModule
    }));
  };

  const handleUpdatePhase = (jobID, nextPhase) => () => {
    dispatch(new UpdateJobPhase(jobID, nextPhase));
  };

  const handleComplete = jobID => () => {
    dispatch(new CompleteJob(jobID));
  };

  const handleRemove = jobID => () => {
    dispatch(new RemoveJob(jobID));
  };

  const handleCancel = jobID => () => {
    dispatch(new CancelJob(jobID));
  };

  const handleDuplicate = () => {
    dispatch(new OpenModal(ModalName.DuplicateJob, { job } as EditJobDataBag));
  };

  const handleViewChat = () => {
    history.push(`/chat/${job.chat_channel}`);
  };

  const canStart = !enableViewOnlyMode && !phase;
  const canEdit = !enableViewOnlyMode && !completed;
  const canAssign = (!enableViewOnlyMode || enableAssignJobsMode) && !completed;
  const canRemove = !enableViewOnlyMode && start_time === undefined;
  const canUpdateStatus = !enableViewOnlyMode && phase && !completed;
  const canComplete = !enableViewOnlyMode && phase && !completed;
  const canCopy = job?.invite_code !== undefined;
  const canCancel = !enableViewOnlyMode && !canRemove && !completed;
  const canViewImages = images && images.length > 0;
  const canDuplicate = !enableViewOnlyMode && true;

  const hasActions = canStart || canEdit || canAssign || canRemove || canUpdateStatus || canComplete || canCopy || canCancel || canDuplicate;

  if (!hasActions) {
    return null;
  }

  return (
    <>
      <CDropdown>
        <CDropdownToggle
          caret
          size='xs'
          color='primary'
          className='bg-color-orange border-0 position-relative'
        >
          Actions
          {
            canViewChat && chat?.loaded && chat?.unreadMessagesCount ? (
              <CBadge className="action-unread-badge">{chat.unreadMessagesCount}</CBadge>
            ) : null
          }
        </CDropdownToggle>
        <Portal>
          <CDropdownMenu>
            { canCopy && (
              <>
                <CDropdownItem>
                  <CopyToClipboard text={ `${ process.env.INVITE_URL }/${job.invite_code}` }>
                    <span>Copy Invite {job.invite_code}</span>
                  </CopyToClipboard>
                </CDropdownItem>
              </>
            ) }
            {
              canViewImages && <CDropdownItem onClick={ () => dispatch(new OpenModal(ModalName.ViewJobImages, { job } as ViewJobImagesDataBag)) }>
                View {job.images?.length > 1 ? 'Photos' : capitalize(job.images?.[0].type)}
              </CDropdownItem>
            }
            {
              canViewChat && <CDropdownItem onClick={handleViewChat}>
                View Chat
                {
                  chat?.loaded && chat?.unreadMessagesCount ? (
                    <CBadge color="danger" className="ml-1">{chat.unreadMessagesCount}</CBadge>
                  ) : null
                }
              </CDropdownItem>
            }
            { canStart && <CDropdownItem onClick={ handleStart(id) }>Start</CDropdownItem> }
            { (canUpdateStatus && JobPhaseOrder[phase]) && <CDropdownItem onClick={ handleUpdatePhase(id, JobPhaseOrder[phase]) }>Mark { JobPhaseLabel[JobPhaseOrder[phase]] }</CDropdownItem> }
            { canComplete && <CDropdownItem onClick={ handleComplete(id) }>Mark Complete</CDropdownItem> }
            { (canEdit || canAssign) && <CDropdownItem divider /> }
            { canEdit && (
              <CDropdownItem onClick={ () => dispatch(new OpenModal(ModalName.EditJob, { job } as EditJobDataBag)) }>Edit</CDropdownItem>
            ) }
            { canDuplicate && (
              <CDropdownItem onClick={ handleDuplicate }>Duplicate</CDropdownItem>
            ) }
            { canAssign && (
              <CDropdownItem onClick={ () => dispatch(new OpenModal(ModalName.ReassignJob, { jobs: [job] } as ReassignJobDataBag)) }>
                { agent_id === -1 ? 'Assign' : 'Reassign' }
              </CDropdownItem>
            ) }
            { canRemove && (
              <>
                <CDropdownItem divider />
                <CDropdownItem onClick={ handleRemove(id) }>Remove</CDropdownItem>
              </>
            ) }
            { canCancel && (
              <>
                <CDropdownItem divider />
                <CDropdownItem onClick={ handleCancel(id) }>Cancel</CDropdownItem>
              </>
            ) }
          </CDropdownMenu>
        </Portal>
      </CDropdown>
    </>
  );
};

export default JobActions;
